import { useState, type ReactNode } from "react";
import { Link, useParams } from "@tanstack/react-router";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { Button, Icon } from "@bfc-software/pallet";

import styles from "./deliver-manage-sidebar.module.css";

interface NavEntry {
  /** Stable key, and what `data-nav-item` carries for tests. */
  id: string;
  /** Key under `manage.sidebar.links` in the deliver namespace. */
  labelKey: string;
  icon: ReactNode;
  /** Route path, carrying `$lang`, `$org` and `$tenant`. */
  to: string;
}

const NAV: ReadonlyArray<NavEntry> = [
  {
    id: "drivers",
    labelKey: "drivers",
    icon: <Icon name="Users" size={18} />,
    to: "/$lang/orgs/$org/deliver/manage/$tenant/drivers",
  },
  {
    id: "settings",
    labelKey: "settings",
    icon: <Icon name="Sliders" size={18} />,
    to: "/$lang/orgs/$org/deliver/manage/$tenant/settings",
  },
  {
    id: "questions",
    labelKey: "questions",
    icon: <Icon name="CheckSquare" size={18} />,
    to: "/$lang/orgs/$org/deliver/manage/$tenant/questions",
  },
];

/**
 * Manage Warehouse's navigation rail.
 *
 * The same shape as Outbound's (`OutboundSidebar`): persistent beside the
 * content, collapsible to icons. Flat rather than grouped, since the section is
 * three screens. Its own copy rather than a shared one until a third rail
 * shows which parts are the pattern and which are Outbound's.
 */
export function DeliverManageSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { lang, org, tenant } = useParams({ strict: false });
  const { t } = useTranslation({
    lang: lang ?? "",
    namespace: TRANSLATION_NAMESPACE.deliver,
  });

  // Every destination needs all three; without them the router cannot build
  // the links.
  if (
    typeof lang !== "string" ||
    typeof org !== "string" ||
    typeof tenant !== "string"
  ) {
    return <></>;
  }

  const params = { lang, org, tenant };

  return (
    <nav
      className={styles.rail}
      data-collapsed={collapsed}
      aria-label={t("manage.sidebar.ariaLabel")}
      data-test-role="deliver-manage-sidebar"
    >
      <Link
        to="/$lang/orgs/$org/deliver"
        params={{ lang, org }}
        className={`${styles.entry} ${styles.back} gap-8`}
        data-nav-item="back"
        title={collapsed ? t("manage.sidebar.back") : undefined}
      >
        <span className={styles.icon}>
          <Icon name="ChevronLeft" size={18} />
        </span>
        <span className={styles.label}>{t("manage.sidebar.back")}</span>
      </Link>

      {NAV.map((entry) => {
        const label = t(`manage.sidebar.links.${entry.labelKey}`);
        return (
          <Link
            key={entry.id}
            to={entry.to}
            params={params}
            className={`${styles.entry} gap-8`}
            activeProps={{ className: styles.active }}
            data-nav-item={entry.id}
            title={collapsed ? label : undefined}
          >
            <span className={styles.bar} aria-hidden="true" />
            <span className={styles.icon}>{entry.icon}</span>
            <span className={styles.label}>{label}</span>
          </Link>
        );
      })}

      <div className={styles.spacer} />

      <div className={styles.collapseToggleSlot}>
        <Button
          kind="icon-only"
          variant="tertiary"
          icon={collapsed ? "ChevronRight" : "ChevronLeft"}
          label={t(
            collapsed ? "manage.sidebar.expand" : "manage.sidebar.collapse",
          )}
          aria-expanded={!collapsed}
          onPress={() => setCollapsed((previous) => !previous)}
        />
      </div>
    </nav>
  );
}
